import { useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Form, Input, Select, Typography } from 'antd';
import type { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import tw from 'twin.macro';

import { PATH } from '@/Route';
import Card from '@/components/Card';
import ErrorMsg from '@/components/formItem/ErrorMsg';
import { ProductItemBox } from '@/components/product/ProductItem';
import BankTransferItem from '@/components/product/ProductItem/BankTransferItem';
import { useOrderProduct } from '@/hooks/mutations/partner';
import { useMe } from '@/hooks/queries/universal';
import type { GetProductResponse, OrderProductParams } from '@/types/partner';
import { getQueryKey } from '@/utils/queryKeys';

interface FormType extends OrderProductParams {
  agreement: boolean;
  personalInfoConsent: boolean;
  thirdPartyConsent: boolean;
}

const ProductOrder = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { state: data } = useLocation() as { state: GetProductResponse };
  const user = useMe();
  const [form] = Form.useForm<FormType>();
  const orderProduct = useOrderProduct({
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey('getProductOrders'),
      });

      queryClient.invalidateQueries({
        queryKey: getQueryKey('admin:getProductOrders'),
      });

      navigate(PATH.MARKETING_PRODUCT.SUCCESS, {
        state: { ...data, orderId: id },
        replace: true,
      });
    },
  });

  useEffect(() => {
    if (user.data?.branches) {
      form.setFieldValue('branchId', user.data.branches[0].id);
    }
    if (data) {
      form.setFieldValue('productId', data.id);
    }
  }, [data, user]);

  const handleSubmit = ({
    productId,
    branchId,
    additionalDescriptionRespond,
  }: FormType) => {
    orderProduct.mutate({ productId, branchId, additionalDescriptionRespond });
  };

  const handleSubmitFailed = ({
    errorFields,
  }: ValidateErrorEntity<FormType>) => {
    const firstError = errorFields?.[0]?.name?.[0];
    if (firstError) form.scrollToField(firstError);
  };

  const syncAgreementCheckboxes = (
    changedValues: Partial<FormType>,
    allValues: FormType
  ) => {
    if ('agreement' in changedValues) {
      const isChecked = changedValues.agreement;
      form.setFieldsValue({
        personalInfoConsent: isChecked,
        thirdPartyConsent: isChecked,
      });
      form.validateFields(['agreement']);
    }

    if (
      'personalInfoConsent' in changedValues ||
      'thirdPartyConsent' in changedValues
    ) {
      const { personalInfoConsent, thirdPartyConsent } = allValues;
      const allChecked = personalInfoConsent && thirdPartyConsent;
      if (form.getFieldValue('agreement') !== allChecked) {
        form.setFieldValue('agreement', allChecked);
        form.validateFields(['agreement']);
      }
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={syncAgreementCheckboxes}
      onFinish={handleSubmit}
      onFinishFailed={handleSubmitFailed}
      scrollToFirstError
      css={tw`[.ant-form-item]:(m-0 w-full)`}
    >
      <section css={tw`flex flex-col gap-y-[48px]`}>
        <Card title="주문자">
          <Typography.Text css={tw`text-[16px] font-semibold mb-[8px]`}>
            {user.data?.name}
          </Typography.Text>
          <Typography.Text>{user.data?.phone}</Typography.Text>
          <Typography.Text css={tw`mt-[4px] mb-[12px]`}>
            {user.data?.email}
          </Typography.Text>

          <Form.Item
            name="branchId"
            rules={[{ required: true, message: '지점을 선택해주세요.' }]}
          >
            <Select placeholder="지점 선택" style={{ width: '100%' }}>
              {user.data?.branches.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Card>

        <Form.Item
          name="productId"
          rules={[{ required: true, message: '상품을 선택해주세요.' }]}
        >
          <ProductItemBox
            productId={data.id}
            title="신청 상품"
            url={data.bannerImageUrl}
            name={data.name}
            summary={data.summary}
            price={data.price}
            discountPrice={data.discountPrice}
          />
        </Form.Item>

        {data.additionalDescription && (
          <Card title="추가 입력 문항">
            <Typography.Text
              css={tw`text-[16px] font-semibold text-[#000] mb-[8px]`}
            >
              {data.additionalDescription}
            </Typography.Text>

            <Form.Item
              name="additionalDescriptionRespond"
              rules={[
                { required: true, message: '추가 입력 문항을 작성해주세요.' },
              ]}
            >
              <Input.TextArea
                placeholder="내용을 입력해주세요."
                showCount
                rows={4}
                maxLength={300}
              />
            </Form.Item>
          </Card>
        )}

        <Card title="결제 수단">
          <Checkbox checked>계좌 이체</Checkbox>
          <BankTransferItem customCss={tw`pl-[24px]`} />
        </Card>

        <Card title="약관 동의">
          <div css={tw`flex flex-col items-start gap-[8px]`}>
            <Form.Item name="agreement" valuePropName="checked" noStyle>
              <Checkbox>주문 내용 확인 및 결제 동의</Checkbox>
            </Form.Item>

            <Form.Item
              name="personalInfoConsent"
              valuePropName="checked"
              noStyle
            >
              <Checkbox style={{ marginLeft: 24 }}>
                (필수) 개인정보 수집, 이용 동의
                <Button
                  type="link"
                  href={`${PATH.POLICY.INDEX}?initAnchor=PURCHASE`}
                  target="_blank"
                  css={tw`underline px-[8px] h-[22px]`}
                >
                  보기
                </Button>
              </Checkbox>
            </Form.Item>

            <Form.Item name="thirdPartyConsent" valuePropName="checked" noStyle>
              <Checkbox style={{ marginLeft: 24 }}>
                (필수) 개인정보 제 3자 제공 동의
                <Button
                  css={tw`underline px-[8px] h-[22px]`}
                  type="link"
                  href={PATH.POLICY.PRIVATE}
                  target="_blank"
                >
                  보기
                </Button>
              </Checkbox>
            </Form.Item>

            <ErrorMsg name="agreement" msg="약관에 동의해주세요." />
          </div>
        </Card>

        <div css={tw`flex w-full justify-center items-center gap-x-[8px]`}>
          <Button onClick={() => navigate(-1)}>취소</Button>
          <Form.Item noStyle>
            <Button htmlType="submit" type="primary">
              신청하기
            </Button>
          </Form.Item>
        </div>
      </section>
    </Form>
  );
};

export default ProductOrder;
